import React, { useCallback, useEffect, useRef, useState } from "react";
import "./WelocateSidebar.css";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import PhotoCameraOutlinedIcon from "@mui/icons-material/PhotoCameraOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FavoriteBorderOutlinedIcon from "@mui/icons-material/FavoriteBorderOutlined";
import FavoriteIcon from "@mui/icons-material/Favorite";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import CheckIcon from "@mui/icons-material/Check";
import { ReactComponent as ShareIcon } from "../../img/icons/share.svg";
import buyMain from "../../img/buy_main.jpg";
import useHttpNoAuth from "../../hooks/use-http-no-auth";
import WelocateToolbox from "./WelocateToolbox";
import { KeyboardDoubleArrowDown, KeyboardDoubleArrowUp } from "@mui/icons-material";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8000/api/v1";

const SIDEBAR_CITIES = [
  { name: "Amsterdam", lat: 52.37587792280813, lng: 4.90650338501307 },
  { name: "Utrecht",   lat: 52.09089006181871, lng: 5.122323994027422 },
  { name: "Rotterdam", lat: 51.90581441693896, lng: 4.466379056341172 },
];

const SidebarEmpty = ({ selectedCity, onCitySelect, totalCount = 0 }) => {
  return (
    <div className="wl-sidebar-empty">
      <div className="wl-sidebar-empty-desktop">
        <div className="wl-sidebar-empty-icon">
          <PlaceOutlinedIcon />
        </div>
        <h2 className="wl-sidebar-empty-title">Pick a property to begin</h2>
        <p className="wl-sidebar-empty-sub">
          Click any pin on the map and we'll surface a deep brief: risk score,
          hidden costs, and what to verify before you bid.
        </p>
        <div className="wl-sidebar-empty-cities">
          {SIDEBAR_CITIES.map((city) => (
            <button
              key={city.name}
              className={`wl-city-btn ${selectedCity?.name === city.name ? "active" : ""}`}
              onClick={() => onCitySelect && onCitySelect(city)}
            >
              {city.name}
            </button>
          ))}
        </div>
        <div className="wl-sidebar-promo">
          <div className="wl-sidebar-promo-img-wrap">
            <img src={buyMain} alt="Welocate" className="wl-sidebar-promo-img" />
            <div className="wl-sidebar-promo-overlay">
              <span className="wl-sidebar-promo-eyebrow">YOUR MOVE, OUR COMMITMENT</span>
              <h3 className="wl-sidebar-promo-heading">See what we can do for you</h3>
              <p className="wl-sidebar-promo-body">
                From mortgage maths to appraisals, WELOCATE walks beside you the whole way.
              </p>
            </div>
          </div>
          <button className="wl-sidebar-promo-cta">
            Visit welocate.com&nbsp;&nbsp;↗
          </button>
        </div>
      </div>
      <div className="wl-sidebar-empty-mobile">
        <h2 className="wl-sidebar-empty-mobile-title">Pick a property to start</h2>
        <div className="wl-sidebar-empty-mobile-count">
          <span className="wl-pulse-dot" aria-hidden="true" />
          <span>{totalCount} properties on this map</span>
        </div>
        <div className="wl-sidebar-empty-mobile-tools">
          <WelocateToolbox />
        </div>
      </div>
    </div>
  );
};

const ListingCarousel = ({ files, isLoading, listingDetail, onAssignChange, isAssigned = false, onCollapse }) => {
  const [index, setIndex] = React.useState(0);
  const [erroredIds, setErroredIds] = React.useState(new Set());
  const [menuOpen, setMenuOpen] = useState(false);
  const [dragX, setDragX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const dragStartXRef = useRef(null);
  const menuRef = useRef(null);

  const {
    sendRequest: addToMyListings,
    isLoading: addToMyListingsLoading,
  } = useHttpNoAuth();

  const {
    sendRequest: removeFromMyListings,
    isLoading: removeFromMyListingsLoading,
  } = useHttpNoAuth();

  const addToListings = useCallback(() => {
    if (!listingDetail?.id) return;
    setMenuOpen(false);
    addToMyListings(
      { url: `${BACKEND_URL}/realmex/assign/${listingDetail.id}`, method: "PATCH" },
      () => onAssignChange && onAssignChange()
    );
  }, [addToMyListings, listingDetail, onAssignChange]);

  const removeFromListings = useCallback(() => {
    if (!listingDetail?.id) return;
    setMenuOpen(false);
    removeFromMyListings(
      { url: `${BACKEND_URL}/realmex/unassign/${listingDetail.id}`, method: "PATCH" },
      () => onAssignChange && onAssignChange()
    );
  }, [removeFromMyListings, listingDetail, onAssignChange]);

  const isMutating = addToMyListingsLoading || removeFromMyListingsLoading;

  useEffect(() => {
    setIndex(0);
    setErroredIds(new Set());
  }, [files]);

  useEffect(() => {
    if (!menuOpen) return;
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [menuOpen]);

  if (isLoading) return (
    <div className="wl-carousel wl-carousel--skeleton">
      {onCollapse && (
        <button
          type="button"
          className="wl-carousel-back"
          aria-label="Collapse sheet"
          onClick={onCollapse}
        >
          <ChevronLeftIcon />
        </button>
      )}
    </div>
  );

  const images = (files?.filter((f) => f.t_realmex_data_firehose_file_path) ?? [])
    .filter((f) => !erroredIds.has(f.t_realmex_data_firehose_file_id));

  if (images.length === 0) return null;

  const safeIndex = Math.min(index, images.length - 1);
  const prev = () => setIndex((i) => (i - 1 + images.length) % images.length);
  const next = () => setIndex((i) => (i + 1) % images.length);
  const handleError = (id) => {
    setErroredIds((prev) => new Set([...prev, id]));
  };

  const SWIPE_THRESHOLD = 40;
  const handleTouchStart = (e) => {
    if (images.length <= 1 || menuOpen) return;
    dragStartXRef.current = e.touches[0].clientX;
    setIsDragging(true);
  };
  const handleTouchMove = (e) => {
    if (dragStartXRef.current === null) return;
    setDragX(e.touches[0].clientX - dragStartXRef.current);
  };
  const handleTouchEnd = () => {
    if (dragStartXRef.current === null) return;
    const delta = dragX;
    dragStartXRef.current = null;
    setIsDragging(false);
    setDragX(0);
    if (Math.abs(delta) > SWIPE_THRESHOLD) {
      if (delta < 0) next();
      else prev();
    }
  };

  return (
    <div
      className={`wl-carousel ${menuOpen ? "wl-carousel--menu-open" : ""}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >
      {onCollapse && (
        <button
          type="button"
          className="wl-carousel-back"
          aria-label="Collapse sheet"
          onClick={onCollapse}
        >
          <ChevronLeftIcon />
        </button>
      )}
      <div className="wl-carousel-actions">
        <div className="wl-carousel-menu-wrap" ref={menuRef}>
          <button
            className="wl-carousel-action-btn"
            aria-label="Listing options"
            onClick={() => setMenuOpen((o) => !o)}
          >
            {isAssigned ? (
              <FavoriteIcon className="wl-carousel-action-icon" style={{ color: "#e53935" }} />
            ) : (
              <FavoriteBorderOutlinedIcon className="wl-carousel-action-icon" />
            )}
          </button>
          {menuOpen && (
            <div className="wl-carousel-menu" role="menu">
              <div className="wl-carousel-menu-title">Options</div>
              <button
                className="wl-carousel-menu-item"
                role="menuitem"
                onClick={isAssigned ? removeFromListings : addToListings}
                disabled={isMutating}
              >
                {isAssigned ? (
                  <>
                    <DeleteOutlineIcon className="wl-carousel-menu-item-icon" />
                    <span className="wl-carousel-menu-item-text">
                      <span className="wl-carousel-menu-item-title">Remove from My listings</span>
                      <span className="wl-carousel-menu-item-sub">You can re-add it later from the listing page</span>
                    </span>
                  </>
                ) : (
                  <>
                    <AddIcon className="wl-carousel-menu-item-icon" />
                    <span className="wl-carousel-menu-item-text">
                      <span className="wl-carousel-menu-item-title">Add to My listings</span>
                      <span className="wl-carousel-menu-item-sub">Save this listing to your personal list</span>
                    </span>
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
      <img
        key={images[safeIndex].t_realmex_data_firehose_file_id}
        src={images[safeIndex].t_realmex_data_firehose_file_path}
        alt={`listing ${safeIndex + 1}`}
        className="wl-carousel-img"
        style={{
          transform: dragX !== 0 ? `translateX(${dragX}px)` : undefined,
          transition: isDragging ? "none" : "transform 0.25s ease",
        }}
        draggable={false}
        onError={() => handleError(images[safeIndex].t_realmex_data_firehose_file_id)}
      />
      {images.length > 1 && (
        <>
          <button className="wl-carousel-btn wl-carousel-btn--prev" onClick={prev} aria-label="Previous">
            <ChevronLeftIcon />
          </button>
          <button className="wl-carousel-btn wl-carousel-btn--next" onClick={next} aria-label="Next">
            <ChevronRightIcon />
          </button>
          <span className="wl-carousel-counter">
            <PhotoCameraOutlinedIcon className="wl-carousel-counter-icon" />
            {safeIndex + 1} / {images.length}
          </span>
        </>
      )}
    </div>
  );
};

const STATUS_COLORS = {
  for_sale: "#1e3a6e",
  under_bid: "#E27C00",
  under_option: "#E27C00",
  sold: "#9CA3AF",
  sold_under_reservation: "#9CA3AF",
};

const STATUS_LABELS = {
  for_sale: "For Sale",
  under_bid: "Under Bid",
  under_option: "Under Option",
  sold: "Sold",
  sold_under_reservation: "Sold (Reservation)",
};

const ENERGY_LABEL_KEYS = ["Minimaal Energie Label", "Minimaal Energielabel", "Minimum Energy Label"];
const ATTRIBUTE_TAG_KEYS = ["Lift beschikbaar"];
const BEDROOM_KEYS = ["Bedrooms", "Slaapkamers"];
const BATHROOM_KEYS = ["Bathrooms", "Badkamers"];
const BUILD_YEAR_KEYS = ["Bouwjaar", "Building Year"];

const formatArea = (value) => {
  if (value == null) return "—";
  return String(value).replace(/m2\b/gi, "m²");
};

const ListingInfo = ({ listing, detail, onCompareClick, isInCompare, compareFull }) => {
  const title = detail?.title || listing?.title;
  const city = detail?.city || listing?.city;
  const postal = detail?.postal || listing?.postal;
  const price = detail?.price ?? listing?.price;
  const priceMain = detail?.price_main || listing?.price_main;
  const soldStatus = detail?.sold_status || listing?.sold_status;
  const publicUrl = detail?.public_url || listing?.public_url;

  const priceK = price != null
    ? price >= 1_000_000
      ? `€${parseFloat((price / 1_000_000).toFixed(1))}m`
      : `€${Math.round(price / 1000)}k`
    : null;
  const priceFull = price != null
    ? `${priceMain ? ` ${priceMain}` : ""}`
    : null;

  const attributes = detail?.attributes || listing?.attributes || [];
  const energyAttr = attributes.find((a) => ENERGY_LABEL_KEYS.includes(a.key));
  const otherTags = ATTRIBUTE_TAG_KEYS
    .map((key) => attributes.find((a) => a.key === key))
    .filter(Boolean);

  const hasTags = energyAttr || otherTags.length > 0;

  const bedroomsAttr = attributes.find((a) => BEDROOM_KEYS.includes(a.key));
  const bathroomsAttr = attributes.find((a) => BATHROOM_KEYS.includes(a.key));
  const buildYearAttr = attributes.find((a) => BUILD_YEAR_KEYS.includes(a.key));
  const area = detail?.area ?? listing?.area;

  return (
    <div className="wl-listing-info">
      <div className="wl-listing-info-top">
        <div className="wl-listing-info-left">
          {title && <h2 className="wl-listing-title">{title}</h2>}
          {(postal || city) && (
            <p className="wl-listing-location">{[postal, city].filter(Boolean).join(" ")}</p>
          )}
        </div>
        <div className="wl-listing-info-right">
          {priceK && <span className="wl-listing-price">{priceFull}</span>}
          {/* {priceFull && <span className="wl-listing-price-sub">{priceFull}</span>} */}
        </div>
      </div>
      <div className="wl-listing-view-btn-row">
        <button
          className="wl-listing-view-btn"
          onClick={() => publicUrl && window.open(publicUrl, "_blank", "noopener,noreferrer")}
          disabled={!publicUrl}
        >
          <span className="wl-listing-view-btn-text--desktop">View listing</span>
          <span className="wl-listing-view-btn-text--mobile">View</span>
          <OpenInNewIcon className="wl-listing-view-btn-icon" />
        </button>
        <button
          className={`wl-listing-share-btn ${isInCompare ? "wl-listing-share-btn--added" : ""} ${!isInCompare && compareFull ? "wl-listing-share-btn--disabled" : ""}`}
          aria-label="Compare"
          onClick={onCompareClick}
          disabled={isInCompare || compareFull}
          title={!isInCompare && compareFull ? "You can compare up to 3 listings" : undefined}
        >
          {isInCompare ? (
            <CheckIcon className="wl-listing-share-btn-icon" />
          ) : (
            <CompareArrowsIcon className="wl-listing-share-btn-icon" />
          )}
          {isInCompare ? "Comparing" : "Compare"}
        </button>
        <button className="wl-listing-share-btn" aria-label="Share">
          <ShareIcon className="wl-listing-share-btn-icon" />
          Share
        </button>
      </div>
      {(soldStatus || hasTags) && (
        <div className="wl-listing-tags wl-listing-tags--row">
          {soldStatus && STATUS_COLORS[soldStatus] && (
            <span
              className="badge wl-status-badge"
              style={{ borderColor: STATUS_COLORS[soldStatus], color: STATUS_COLORS[soldStatus] }}
            >
              <span
                className="wl-status-dot"
                style={{ backgroundColor: STATUS_COLORS[soldStatus] }}
              />
              {STATUS_LABELS[soldStatus] ?? soldStatus}
            </span>
          )}
          {energyAttr && (
            <span className="badge energy">Energielabel {energyAttr.value}</span>
          )}
          {otherTags.map((attr) => (
            <span key={attr.key} className="wl-listing-tag">{attr.value}</span>
          ))}
        </div>
      )}
      <div className="wl-listing-stats">
        <div className="wl-listing-stat">
          <span className="wl-listing-stat-value">{bedroomsAttr?.value ?? "—"}</span>
          <span className="wl-listing-stat-label">Bedrooms</span>
        </div>
        <div className="wl-listing-stat">
          <span className="wl-listing-stat-value">{bathroomsAttr?.value ?? "—"}</span>
          <span className="wl-listing-stat-label">Bathrooms</span>
        </div>
        <div className="wl-listing-stat">
          <span className="wl-listing-stat-value">{formatArea(area)}</span>
          <span className="wl-listing-stat-label">Living area</span>
        </div>
        <div className="wl-listing-stat">
          <span className="wl-listing-stat-value">{buildYearAttr?.value ?? "—"}</span>
          <span className="wl-listing-stat-label">Built</span>
        </div>
      </div>
    </div>
  );
};

const AnalyzingStatus = () => (
  <div className="wl-analyzing-status" role="status" aria-live="polite">
    <span className="wl-analyzing-dot" aria-hidden="true" />
    <span className="wl-analyzing-text">We are analyzing this listing</span>
  </div>
);

const ListingDescription = ({ description, isLoading }) => {
  if (!isLoading && !description) return null;
  return (
    <div className="wl-listing-description">
      <div className="wl-listing-section-label">
        <span className="wl-listing-section-dot" />
        DESCRIPTION
      </div>
      {isLoading ? (
        <div className="wl-skeleton-block">
          <AnalyzingStatus />
          <div className="wl-skeleton-lines">
            <span className="wl-skeleton-line" />
            <span className="wl-skeleton-line" />
            <span className="wl-skeleton-line wl-skeleton-line--short" />
          </div>
        </div>
      ) : (
        <p className="wl-listing-description-text">{description}</p>
      )}
    </div>
  );
};

const INSIGHT_ICON = {
  green: CheckCircleOutlineIcon,
  orange: WarningAmberOutlinedIcon,
  blue: LightbulbOutlinedIcon,
  red: ErrorOutlineIcon,
};

const CLASSIFICATION_LEVEL_COLOR = {
  0: "green",
  1: "blue",
  2: "orange",
  3: "red",
};

const SmartInsights = ({ insights, isLoading }) => {
  if (!isLoading && (!insights || insights.length === 0)) return null;
  return (
    <div className="wl-listing-description wl-smart-insights">
      <div className="wl-listing-section-label">
        <span className="wl-listing-section-dot" />
        SMART INSIGHTS
      </div>
      <div className="wl-insights-list">
        {isLoading ? (
          <div className="wl-skeleton-block">
            <AnalyzingStatus />
            <div className="wl-skeleton-cards">
              <div className="wl-skeleton-card" />
              <div className="wl-skeleton-card" />
              <div className="wl-skeleton-card" />
            </div>
          </div>
        ) : (
          insights.map((insight, i) => {
            const color = CLASSIFICATION_LEVEL_COLOR[insight.classification_level] || "blue";
            const Icon = INSIGHT_ICON[color];
            return (
              <div key={i} className={`wl-insight-card wl-insight-card--${color}`}>
                <Icon className="wl-insight-icon" />
                <div>
                  <p className="wl-insight-title">{insight.title}</p>
                  <p className="wl-insight-subtitle">{insight.subtitle}</p>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const ListingMobilePeek = ({ listing, detail, isLoading, onClose, onViewDetails }) => {
  const price = detail?.price ?? listing?.price;
  const priceFormatted = price != null ? `€${price.toLocaleString("nl-NL")}` : null;
  const address = detail?.title || listing?.title || [detail?.postal || listing?.postal, detail?.city || listing?.city].filter(Boolean).join(" ");
  const city = detail?.city || listing?.city;
  const soldStatus = detail?.sold_status || listing?.sold_status;
  const statusColor = soldStatus ? STATUS_COLORS[soldStatus] : null;
  const statusLabel = soldStatus ? (STATUS_LABELS[soldStatus] ?? soldStatus) : null;

  const attributes = detail?.attributes || listing?.attributes || [];
  const bedroomsAttr = attributes.find((a) => BEDROOM_KEYS.includes(a.key));
  const area = detail?.area ?? listing?.area;
  const firstImage = detail?.files?.find((f) => f.t_realmex_data_firehose_file_path);

  const metaParts = [
    bedroomsAttr?.value != null ? `${bedroomsAttr.value} bd` : null,
    area != null ? formatArea(area) : null,
  ].filter(Boolean);

  if (isLoading) {
    return (
      <div className="wl-peek wl-peek--loading">
        <div className="wl-peek-row">
          <div className="wl-peek-avatar wl-peek-skeleton" />
          <div className="wl-peek-content">
            <div className="wl-peek-skeleton wl-peek-skeleton--price" />
            <div className="wl-peek-skeleton wl-peek-skeleton--address" />
            <div className="wl-peek-skeleton wl-peek-skeleton--meta" />
          </div>
          <button
            type="button"
            className="wl-peek-close"
            aria-label="Close"
            onClick={onClose}
          >
            <CloseIcon fontSize="small" />
          </button>
        </div>
        <button type="button" className="wl-peek-cta" onClick={onViewDetails}>
          View more <KeyboardDoubleArrowUp className="wl-peek-cta-icon" />
        </button>
      </div>
    );
  }

  return (
    <div className="wl-peek">
      <div className="wl-peek-row">
        <div className="wl-peek-avatar">
          {firstImage && (
            <img
              src={firstImage.t_realmex_data_firehose_file_path}
              alt="listing"
              className="wl-peek-avatar-img"
            />
          )}
        </div>
        <div className="wl-peek-content">
          {priceFormatted && <div className="wl-peek-price">{priceFormatted}</div>}
          {address && <div className="wl-peek-address">{address}</div>}
          {(statusLabel || metaParts.length > 0) && (
            <div className="wl-peek-meta">
              {statusLabel && (
                <span className="wl-peek-status" style={{ color: statusColor || undefined }}>
                  <span
                    className="wl-peek-status-dot"
                    style={{ background: statusColor || "#2f4b9b" }}
                  />
                  {statusLabel}
                </span>
              )}
              {metaParts.map((part, i) => (
                <React.Fragment key={i}>
                  <span className="wl-peek-meta-sep">·</span>
                  <span className="wl-peek-meta-part">{part}</span>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
        <button
          type="button"
          className="wl-peek-close"
          aria-label="Close"
          onClick={onClose}
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>
      <button type="button" className="wl-peek-cta" onClick={onViewDetails}>
        View more <KeyboardDoubleArrowUp className="wl-peek-cta-icon" />
      </button>
    </div>
  );
};

const WelocateSidebar = ({ selectedListing, selectedCity, onCitySelect, onClearSelection, refetch, totalCount = 0, expandRequest = 0, collapseRequest = 0, selectionSource = null, onBackToAi, onAddToCompare, compareList = [] }) => {
  const [listingDetail, setListingDetail] = useState(null);
  const [smartDescription, setSmartDescription] = useState(null);
  const [smartInsights, setSmartInsights] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const compareKey = (l) => `${l?.lat}-${l?.lng}`;
  const isInCompare = selectedListing
    ? compareList.some((l) => compareKey(l) === compareKey(selectedListing))
    : false;
  const compareFull = compareList.length >= 3;
  const handleCompareClick = useCallback(() => {
    if (!selectedListing || isInCompare || compareFull) return;
    const merged = { ...(listingDetail || {}), ...selectedListing };
    onAddToCompare && onAddToCompare(merged);
  }, [selectedListing, isInCompare, compareFull, onAddToCompare, listingDetail]);

  const { sendRequest: getListingDetail } = useHttpNoAuth();
  const { sendRequest: getSmartInsights } = useHttpNoAuth();

  const fetchDetail = useCallback((lat, lng) => {
    getListingDetail(
      { url: `${BACKEND_URL}/realmex/get_by_coordinates?lat=${lat}&lng=${lng}`, method: "GET" },
      (data) => setListingDetail(data)
    );
  }, [getListingDetail]);

  const fetchSmartInsights = useCallback((lat, lng) => {
    getSmartInsights(
      { url: `${BACKEND_URL}/realmex/get_smart_insights?lat=${lat}&lng=${lng}`, method: "GET" },
      (data) => {
        setSmartDescription(data?.smart_description ?? null);
        setSmartInsights(data?.smart_insights ?? []);
      }
    );
  }, [getSmartInsights]);

  useEffect(() => {
    if (selectedListing?.lat != null && selectedListing?.lng != null) {
      setListingDetail(null);
      setSmartDescription(null);
      setSmartInsights(null);
      fetchDetail(selectedListing.lat, selectedListing.lng);
      fetchSmartInsights(selectedListing.lat, selectedListing.lng);
    } else {
      setListingDetail(null);
      setSmartDescription(null);
      setSmartInsights(null);
    }
  }, [selectedListing, fetchDetail, fetchSmartInsights]);

  useEffect(() => {
    if (expandRequest > 0) setIsExpanded(true);
  }, [expandRequest]);

  useEffect(() => {
    if (collapseRequest > 0) setIsExpanded(false);
  }, [collapseRequest]);

  return (
    <aside
      className={`wl-sidebar ${isExpanded ? "wl-sidebar--expanded" : ""} ${
        selectedListing ? "wl-sidebar--has-selection" : ""
      }`}
    >
      {selectedListing && (
        <ListingMobilePeek
          listing={selectedListing}
          detail={listingDetail}
          isLoading={!listingDetail}
          onClose={() => {
            setIsExpanded(false);
            onClearSelection && onClearSelection();
          }}
          onViewDetails={() => setIsExpanded(true)}
        />
      )}
      {selectedListing && (
        <div className="wl-sidebar-full">
          <ListingCarousel
            files={listingDetail?.files}
            listingDetail={listingDetail}
            onAssignChange={() => {
              if (selectedListing?.lat != null && selectedListing?.lng != null) {
                fetchDetail(selectedListing.lat, selectedListing.lng);
                refetch && refetch();
              }
            }}
            isLoading={!listingDetail}
            isAssigned={listingDetail?.is_assigned ?? selectedListing?.is_assigned}
            onCollapse={() => {
              if (selectionSource === "ai" && onBackToAi) {
                onBackToAi();
              } else {
                setIsExpanded(false);
              }
            }}
          />
          <ListingInfo
            listing={selectedListing}
            detail={listingDetail}
            onCompareClick={handleCompareClick}
            isInCompare={isInCompare}
            compareFull={compareFull}
          />
          <ListingDescription
            description={smartDescription}
            isLoading={smartDescription === null && smartInsights === null}
          />
          <SmartInsights
            insights={smartInsights}
            isLoading={smartInsights === null}
          />
        </div>
      )}
      {selectedListing && (
        <div className="wl-sidebar-mobile-tools">
          <WelocateToolbox />
        </div>
      )}
      <div className="wl-sidebar-list">
        {!selectedListing && (
          <SidebarEmpty
            selectedCity={selectedCity}
            onCitySelect={onCitySelect}
            totalCount={totalCount}
          />
        )}
      </div>
    </aside>
  );
};

export default WelocateSidebar;
